import { Workspace } from '@/features/iam/workspaces/Workspace'
import { action, observable } from 'mobx'
import { once } from 'lodash-es'
import { Model, type ModelProps } from '@/infra/model'
import { unixEpoch } from '@/utils/date-util'
import { collection } from '@/infra/collection'
import {
  BillingMethod,
  type BillingMethodProps,
} from '@/features/billing/profile/BillingMethod'
import { Id } from '@/infra/id'

export type BillingProfileProps = ModelProps<BillingProfile> & {
  billingMethods?: Partial<BillingMethodProps>[]
}

/**
 * The billing methods of a member or a team.
 */
export class BillingProfile extends Model<BillingProfile> {
  id = Id.none
  userId: Id | null = Id.none
  userPublicId: Id | null = Id.none
  teamId: Id | null = Id.none

  @observable accessor referralCouponCode = ''
  @observable accessor referralCouponId = Id.none
  @observable accessor dateCreated = unixEpoch()
  @observable accessor workspace = Workspace.none
  @observable accessor dateModified = unixEpoch()
  @observable accessor defaultBillingMethodId: Id | null = Id.none
  @observable accessor defaultPayoutMethodId: Id | null = Id.none

  readonly billingMethods = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new BillingMethod(this),
  })

  /**
   * Default instance.
   */
  static get none() {
    return lazyNone()
  }

  /** @inheritDoc */
  @action.bound
  override set({
    billingMethods,
    ...rest
  }: Partial<BillingProfileProps>): this {
    if (billingMethods) {
      this.billingMethods.reset(billingMethods)
    }
    return super.set(rest)
  }
}

const lazyNone = once(() => new BillingProfile())
